import type { CheckInListRow } from '@peles/shared';
import type { CheckInBody } from '../api/invites';
import type { CheckInTarget } from '../hooks/useInvites';

/** A check-in made while the door had no connection, waiting to be sent. */
export interface QueuedCheckIn {
  id: string;
  target: CheckInTarget;
  body: CheckInBody;
  /** When it was scanned — sent as `at`, so the arrival keeps its own time. */
  queuedAt: string;
  /** What the operator was told, for the pending list ("Sarah Levi"). */
  label: string;
  /** How the party's row shows meanwhile — kept, so a reload still shows it arrived. */
  optimistic?: Partial<CheckInListRow>;
}

const keyFor = (eventId: string) => `checkin-queue:${eventId}`;

/**
 * The queue for one event, oldest first. Storage that cannot be read — a
 * private window, a full disk — reads as empty rather than throwing: the door
 * keeps working online.
 */
export function readCheckInQueue(eventId: string): QueuedCheckIn[] {
  try {
    const raw = localStorage.getItem(keyFor(eventId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter(
          (item): item is QueuedCheckIn =>
            !!item &&
            typeof item === 'object' &&
            typeof (item as QueuedCheckIn).id === 'string' &&
            !!(item as QueuedCheckIn).target,
        )
      : [];
  } catch {
    return [];
  }
}

function writeCheckInQueue(eventId: string, queue: QueuedCheckIn[]): boolean {
  try {
    if (queue.length === 0) localStorage.removeItem(keyFor(eventId));
    else localStorage.setItem(keyFor(eventId), JSON.stringify(queue));
    return true;
  } catch {
    return false;
  }
}

let counter = 0;

/** Keep a check-in to send later. False when it could not be kept at all. */
export function enqueueCheckIn(
  eventId: string,
  item: Omit<QueuedCheckIn, 'id' | 'queuedAt'>,
  now: Date = new Date(),
): QueuedCheckIn | null {
  counter += 1;
  const queued: QueuedCheckIn = {
    ...item,
    id: `${now.getTime()}-${counter}`,
    queuedAt: now.toISOString(),
  };
  return writeCheckInQueue(eventId, [...readCheckInQueue(eventId), queued]) ? queued : null;
}

/** Drop a check-in once it has been sent (or refused for good). */
export function removeQueuedCheckIn(eventId: string, id: string): void {
  writeCheckInQueue(
    eventId,
    readCheckInQueue(eventId).filter((item) => item.id !== id),
  );
}

/**
 * Whether a failed request means "no connection" — worth keeping and trying
 * again — rather than the server refusing it, which a retry will not change.
 */
export function isOfflineError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (!err || typeof err !== 'object') return false;
  const e = err as { response?: unknown; code?: string; message?: string };
  if (e.response) return false;
  return e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED' || /network/i.test(e.message ?? '');
}
